// Auth Context Provider using Supabase Auth
// Provides authentication state and functions to the entire app

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js'
import {
  supabase,
  AuthUser,
  AuthSession,
  toAuthUser,
  toAuthSession,
  signOut as supabaseSignOut
} from '../lib/supabase-auth'
import type { Database } from '../../database.types'

// Context types
interface AuthContextType {
  // Session state
  session: AuthSession | null
  user: AuthUser | null
  loading: boolean
  isSignedIn: boolean
  isLoaded: boolean

  // Auth functions
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>

  // Supabase client (authenticated)
  supabaseClient: typeof supabase

  // Get access token for API calls
  getAccessToken: () => Promise<string | null>
}

// Create context with undefined default
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)

  // Get access token for API calls
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  }, [])

  // Sign out function
  const signOut = useCallback(async () => {
    try {
      await supabaseSignOut()
      setSession(null)
      setUser(null)
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }, [])

  // Refresh session manually
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) throw error

      if (data.session) {
        setSession(toAuthSession(data.session))
        setUser(toAuthUser(data.session.user))
      }
    } catch (error) {
      console.error('Error refreshing session:', error)
    }
  }, [])

  // Handle auth state changes
  const handleAuthStateChange = useCallback((event: AuthChangeEvent, session: Session | null) => {
    console.log('[Auth] State change:', event, session?.user?.id)

    const authSession = toAuthSession(session)
    const authUser = toAuthUser(session?.user ?? null)

    setSession(authSession)
    setUser(authUser)
    setLoading(false)
    setIsLoaded(true)
  }, [])

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error) {
          console.error('[Auth] Error getting session:', error)
        }

        handleAuthStateChange('INITIAL_SESSION', session)
      } catch (error) {
        console.error('[Auth] Error initializing auth:', error)
        setLoading(false)
        setIsLoaded(true)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange)

    // Cleanup subscription
    return () => {
      subscription.unsubscribe()
    }
  }, [handleAuthStateChange])

  // Context value
  const value: AuthContextType = {
    session,
    user,
    loading,
    isSignedIn: !!user,
    isLoaded,
    signOut,
    refreshSession,
    supabaseClient: supabase,
    getAccessToken,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuthContext() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }

  return context
}

// Export the context for advanced use cases
export { AuthContext }
export type { AuthContextType }
